import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, CheckCircle, BarChart3, ArrowLeft, Clock, 
  Calendar, Edit3, Eye, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EmployeeSurveys = () => {
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [activeTab, setActiveTab] = useState('todo');
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState({});
  const [publishedResults, setPublishedResults] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/surveys/employee/available`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });
      const data = await response.json();
      setSurveys(data);
    } catch (error) {
      console.error('Error fetching surveys:', error);
      toast.error('Failed to load surveys');
    }
    setLoading(false);
  };

  const todoSurveys = surveys.filter(s => s.response_status === 'not_started' || s.response_status === 'in_progress');
  const completedSurveys = surveys.filter(s => s.response_status === 'completed');
  const resultsAvailable = surveys.filter(s => s.response_status === 'completed' && s.results_published);

  // Check if survey is still open (deadline not passed)
  const isSurveyOpen = (survey) => {
    const now = new Date();
    const dueDate = new Date(survey.due_date);
    return dueDate >= now && survey.status === 'live';
  };

  const startSurvey = async (survey, editing = false) => {
    setIsEditing(editing);
    if (survey.response_id) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${BACKEND_URL}/api/surveys/${survey.id}/my-response`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const existingResponses = {};
          data.responses.forEach(r => {
            existingResponses[r.question_id] = r.answer;
          });
          setResponses(existingResponses);
        }
      } catch (error) {
        console.error('Error loading response:', error);
      }
    } else {
      setResponses({});
    }
    setSelectedSurvey(survey);
  };

  const handleResponseChange = (questionId, answer, questionType) => {
    if (questionType === 'checkbox') {
      const currentAnswers = responses[questionId] || [];
      const newAnswers = currentAnswers.includes(answer)
        ? currentAnswers.filter(a => a !== answer)
        : [...currentAnswers, answer];
      setResponses({ ...responses, [questionId]: newAnswers });
    } else {
      setResponses({ ...responses, [questionId]: answer });
    }
  };

  const saveDraft = async () => {
    try {
      const token = localStorage.getItem('token');
      const responseData = {
        survey_id: selectedSurvey.id,
        responses: Object.entries(responses).map(([question_id, answer]) => ({
          question_id,
          answer
        })),
        is_draft: true
      };

      const response = await fetch(`${BACKEND_URL}/api/surveys/${selectedSurvey.id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(responseData)
      });

      if (response.ok) {
        toast.success('Draft saved successfully!');
        fetchSurveys();
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    }
  };

  const submitSurvey = async () => {
    const unanswered = selectedSurvey.questions.filter(q => 
      q.required && (!responses[q.id] || (Array.isArray(responses[q.id]) && responses[q.id].length === 0))
    );

    if (unanswered.length > 0) {
      toast.error('Please answer all required questions before submitting.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const responseData = {
        survey_id: selectedSurvey.id,
        responses: Object.entries(responses).map(([question_id, answer]) => ({
          question_id,
          answer
        })),
        is_draft: false
      };

      const response = await fetch(`${BACKEND_URL}/api/surveys/${selectedSurvey.id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(responseData)
      });

      if (response.ok) {
        toast.success(isEditing ? 'Survey updated successfully!' : 'Survey submitted successfully!');
        setSelectedSurvey(null);
        setResponses({});
        setIsEditing(false);
        fetchSurveys();
        setActiveTab('completed');
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error submitting survey:', error);
      toast.error('Failed to submit survey');
    }
  };

  const viewPublishedResults = async (surveyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/surveys/${surveyId}/published-results`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPublishedResults(data);
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.detail}`);
      }
    } catch (error) {
      console.error('Error fetching results:', error);
      toast.error('Failed to load results');
    }
  };

  const getDaysRemaining = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diffTime = due - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Survey Form View
  if (selectedSurvey) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6">
          <Button 
            onClick={() => {
              setSelectedSurvey(null);
              setResponses({});
              setIsEditing(false);
            }} 
            variant="ghost" 
            size="sm" 
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Surveys
          </Button>
          
          <div className="flex items-start gap-3">
            <div className={`p-3 rounded-xl flex-shrink-0 ${isEditing ? 'bg-orange-100' : 'bg-purple-100'}`}>
              {isEditing ? (
                <Edit3 className={`w-6 h-6 text-orange-600`} />
              ) : (
                <MessageSquare className={`w-6 h-6 text-purple-600`} />
              )}
            </div>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{selectedSurvey.title}</h1>
              <p className="text-sm text-gray-500 mt-1">{selectedSurvey.description}</p>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                {selectedSurvey.is_anonymous && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                    <Eye className="w-3 h-3" />
                    Anonymous
                  </span>
                )}
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                  <Calendar className="w-3 h-3" />
                  Due: {new Date(selectedSurvey.due_date).toLocaleDateString()}
                </span>
                {isEditing && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                    <Edit3 className="w-3 h-3" />
                    Editing Response
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="divide-y divide-gray-100">
            {selectedSurvey.questions.map((question, index) => (
              <div key={question.id} className="p-4 sm:p-6">
                <div className="mb-3 flex items-center gap-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                    {index + 1}
                  </span>
                  {question.required && (
                    <span className="text-red-500 text-sm">Required</span>
                  )}
                </div>
                <p className="text-base sm:text-lg font-medium text-gray-900 mb-4">{question.question_text}</p>

                {question.question_type === 'multiple_choice' && (
                  <div className="space-y-2">
                    {question.options.map(option => (
                      <label key={option.id} className={`flex items-center p-3 sm:p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        responses[question.id] === option.text 
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}>
                        <input
                          type="radio"
                          name={question.id}
                          value={option.text}
                          checked={responses[question.id] === option.text}
                          onChange={(e) => handleResponseChange(question.id, e.target.value, 'multiple_choice')}
                          className="mr-3 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="text-sm sm:text-base text-gray-700">{option.text}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.question_type === 'checkbox' && (
                  <div className="space-y-2">
                    {question.options.map(option => (
                      <label key={option.id} className={`flex items-center p-3 sm:p-4 border-2 rounded-xl cursor-pointer transition-all ${
                        (responses[question.id] || []).includes(option.text)
                          ? 'border-emerald-500 bg-emerald-50' 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}>
                        <input
                          type="checkbox"
                          value={option.text}
                          checked={(responses[question.id] || []).includes(option.text)}
                          onChange={(e) => handleResponseChange(question.id, e.target.value, 'checkbox')}
                          className="mr-3 h-4 w-4 text-emerald-600 focus:ring-emerald-500 rounded"
                        />
                        <span className="text-sm sm:text-base text-gray-700">{option.text}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.question_type === 'free_text' && (
                  <textarea
                    value={responses[question.id] || ''}
                    onChange={(e) => handleResponseChange(question.id, e.target.value, 'free_text')}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm sm:text-base transition-all"
                    rows="4"
                    placeholder="Enter your response..."
                  />
                )}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="p-4 sm:p-6 bg-gray-50 border-t border-gray-100">
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={saveDraft}
                variant="outline"
                className="w-full sm:w-auto order-2 sm:order-1"
              >
                Save Draft
              </Button>
              <Button
                onClick={submitSurvey}
                className="w-full sm:flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 order-1 sm:order-2"
              >
                {isEditing ? 'Update Response' : 'Submit Survey'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (publishedResults) {
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-md p-4 sm:p-6">
          <Button onClick={() => setPublishedResults(null)} variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Surveys
          </Button>
          
          <div className="flex items-start gap-3">
            <div className="p-3 bg-indigo-100 rounded-xl flex-shrink-0">
              <BarChart3 className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{publishedResults.survey_title}</h1>
              <p className="text-sm text-gray-500 mt-1">{publishedResults.survey_description}</p>
              <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                {publishedResults.total_responses} responses
              </span>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="divide-y divide-gray-100">
            {publishedResults.question_statistics.map((stat, index) => (
              <div key={stat.question_id} className="p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold">
                    {index + 1}
                  </span>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">
                  {stat.question_text}
                </h3>
                
                {stat.answer_distribution && (
                  <div className="space-y-3">
                    {Object.entries(stat.answer_distribution).map(([answer, count]) => (
                      <div key={answer}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 font-medium">{answer}</span>
                          <span className="text-gray-500">{count} ({((count / stat.total_responses) * 100).toFixed(1)}%)</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all"
                            style={{ width: `${(count / stat.total_responses) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {stat.option_distribution && (
                  <div className="space-y-3">
                    {Object.entries(stat.option_distribution).map(([option, count]) => (
                      <div key={option}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 font-medium">{option}</span>
                          <span className="text-gray-500">{count} ({((count / stat.total_responses) * 100).toFixed(1)}%)</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-emerald-500 to-teal-500 h-3 rounded-full transition-all"
                            style={{ width: `${(count / stat.total_responses) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {stat.question_type === 'free_text' && (
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                    {stat.response_count} text responses submitted
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Main List View - Dashboard Style
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Button onClick={() => navigate('/profile')} variant="ghost" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-md text-center">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-gray-800">{todoSurveys.length}</p>
          <p className="text-xs sm:text-sm text-gray-500">To Do</p>
        </div>

        <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-md text-center">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-2">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-gray-800">{completedSurveys.length}</p>
          <p className="text-xs sm:text-sm text-gray-500">Done</p>
        </div>

        <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-md text-center">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto mb-2">
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-gray-800">{resultsAvailable.length}</p>
          <p className="text-xs sm:text-sm text-gray-500">Results</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => setActiveTab('todo')}
            className={`flex-1 px-3 sm:px-6 py-3 text-sm font-medium transition-all ${
              activeTab === 'todo'
                ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            To Do
          </button>
          <button
            onClick={() => setActiveTab('completed')}
            className={`flex-1 px-3 sm:px-6 py-3 text-sm font-medium transition-all ${
              activeTab === 'completed'
                ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            Completed
          </button>
          <button
            onClick={() => setActiveTab('results')}
            className={`flex-1 px-3 sm:px-6 py-3 text-sm font-medium transition-all ${
              activeTab === 'results'
                ? 'text-emerald-600 border-b-2 border-emerald-500 bg-emerald-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            Results
          </button>
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {/* To Do Tab */}
          {activeTab === 'todo' && (
            <>
              {todoSurveys.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">All caught up!</p>
                  <p className="text-sm text-gray-400 mt-1">No surveys to complete</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {todoSurveys.map(survey => {
                    const daysRemaining = getDaysRemaining(survey.due_date);
                    const isUrgent = daysRemaining <= 3;
                    
                    return (
                      <div 
                        key={survey.id} 
                        className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all"
                      >
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg flex-shrink-0 ${isUrgent ? 'bg-red-100' : 'bg-purple-100'}`}>
                            <MessageSquare className={`w-5 h-5 ${isUrgent ? 'text-red-600' : 'text-purple-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900">{survey.title}</h3>
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{survey.description}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                isUrgent ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-600'
                              }`}>
                                <Calendar className="w-3 h-3" />
                                {daysRemaining > 0 ? `${daysRemaining} days left` : 'Due today'}
                              </span>
                              <span className="text-xs text-gray-400">
                                {survey.questions.length} questions
                              </span>
                              {survey.is_anonymous && (
                                <span className="text-xs text-purple-600">Anonymous</span>
                              )}
                              {survey.response_status === 'in_progress' && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
                                  Draft
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => startSurvey(survey)}
                          className="w-full mt-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                        >
                          {survey.response_status === 'in_progress' ? 'Continue' : 'Start Survey'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Completed Tab */}
          {activeTab === 'completed' && (
            <>
              {completedSurveys.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">No completed surveys</p>
                  <p className="text-sm text-gray-400 mt-1">Complete a survey to see it here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {completedSurveys.map(survey => {
                    const canEdit = isSurveyOpen(survey);
                    
                    return (
                      <div 
                        key={survey.id} 
                        className="bg-gray-50 rounded-xl p-4"
                      >
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900">{survey.title}</h3>
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">{survey.description}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                                <CheckCircle className="w-3 h-3" />
                                Completed
                              </span>
                              {canEdit && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                  <Edit3 className="w-3 h-3" />
                                  Editable
                                </span>
                              )}
                              {!canEdit && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full text-xs font-medium">
                                  <AlertCircle className="w-3 h-3" />
                                  Closed
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        {canEdit && (
                          <Button
                            onClick={() => startSurvey(survey, true)}
                            variant="outline"
                            className="w-full mt-3"
                          >
                            <Edit3 className="w-4 h-4 mr-2" />
                            Edit Response
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Results Tab */}
          {activeTab === 'results' && (
            <>
              {resultsAvailable.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BarChart3 className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 font-medium">No results available</p>
                  <p className="text-sm text-gray-400 mt-1">Results will appear when published</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {resultsAvailable.map(survey => (
                    <div 
                      key={survey.id} 
                      className="bg-gray-50 rounded-xl p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg flex-shrink-0">
                          <BarChart3 className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900">{survey.title}</h3>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{survey.description}</p>
                          <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                            <BarChart3 className="w-3 h-3" />
                            Results Published
                          </span>
                        </div>
                      </div>
                      <Button
                        onClick={() => viewPublishedResults(survey.id)}
                        className="w-full mt-3 bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Results
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeSurveys;
